/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  identityApiRef,
  storageApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Entity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import useAsync from 'react-use/lib/useAsync';
import useObservable from 'react-use/lib/useObservable';

const SCOPE_STORAGE_BUCKET = 'home';
const SCOPE_STORAGE_KEY = 'scope';
// Multi-select model: empty array = "My work" (union of ALL the user's ownership).
// A non-empty array narrows to the union of those teams' owned components
// (matches the catalog URL contract `filters[owners]=A&filters[owners]=B`).
// Legacy single-string values from the previous single-select implementation
// are migrated to a one-element array on read.


export const teamRefsFromOwnership = (refs?: string[]): string[] =>
  (refs ?? []).filter(r => r.startsWith('group:'));

// Filter owned entities by selected scopes. Empty -> "My work" -> keep all.
// Non-empty -> entities owned (directly or transitively) by ANY of the selected
// groups (union semantics, matching the catalog URL contract
// `filters[owners]=A&filters[owners]=B` verified empirically against the
// live catalog).
// BFS the group hierarchy downward: returns the input refs plus every
// transitive descendant (sub-team). Cycle-safe. Exported for unit tests.
export const expandScopes = (
  refs: string[],
  childrenMap: Record<string, string[]>,
): string[] => {
  const out = new Set(refs);
  const queue = [...refs];
  while (queue.length > 0) {
    const ref = queue.shift()!;
    for (const child of childrenMap[ref] ?? []) {
      if (!out.has(child)) {
        out.add(child);
        queue.push(child);
      }
    }
  }
  return [...out];
};

// The effective owner-ref set for a scope selection: the selected teams (or
// the user's own ownership refs for "My work"), expanded to transitive
// descendant teams when the sub-team toggle is on. This is THE scope
// predicate -- both the client-side entity filter below and any catalog
// deep-link must derive from it, or counts and link targets drift apart.
export const effectiveScopeRefs = (
  scopes: string[],
  opts?: {
    directRefs?: string[];
    childrenMap?: Record<string, string[]>;
    subTeams?: boolean;
  },
): string[] => {
  const base = scopes.length > 0 ? scopes : opts?.directRefs ?? [];
  return opts?.subTeams
    ? expandScopes(base, opts?.childrenMap ?? {})
    : base;
};

// Filter owned entities by the selected scope set. Empty scopes == "My work"
// (the user's own ownership refs). With subTeams on, the effective set expands
// to every transitive descendant team -- so a parent-team member sees the
// rollup of what their sub-teams own. One definition shared by every Home
// card (Services / Pending work / Open PRs / the in-scope count).
export const filterByScopes = (
  entities: Entity[],
  scopes: string[],
  opts?: {
    directRefs?: string[];
    childrenMap?: Record<string, string[]>;
    subTeams?: boolean;
  },
): Entity[] => {
  const base = scopes.length > 0 ? scopes : opts?.directRefs ?? [];
  if (base.length === 0 && scopes.length === 0) {
    // No metadata to scope by (legacy callers): match everything.
    return entities;
  }
  const wanted = new Set(effectiveScopeRefs(scopes, opts));
  return entities.filter(e =>
    (e.relations ?? []).some(
      rel => rel.type === 'ownedBy' && wanted.has(rel.targetRef),
    ),
  );
};

// Facet counts over the scoped service set, for the Services card's filter
// chips. Values come from spec.type / spec.lifecycle as-is (lowercased);
// entities without the field are skipped for that facet.
export const serviceFacets = (
  entities: Entity[],
  field: 'type' | 'lifecycle',
): { key: string; label: string; count: number }[] => {
  const counts = new Map<string, number>();
  for (const e of entities) {
    const raw = e.spec?.[field];
    if (typeof raw !== 'string' || raw.length === 0) continue;
    const key = raw.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, count]) => ({ key, label: key, count }));
};

// Card-local view filter (type AND lifecycle). A selection that no longer
// exists in the entity set (scope changed underneath it) is ignored rather
// than silently emptying the list.
export const filterServices = (
  entities: Entity[],
  typeFilter: string | null,
  lifecycleFilter: string | null,
): Entity[] => {
  const validType =
    typeFilter && serviceFacets(entities, 'type').some(f => f.key === typeFilter)
      ? typeFilter
      : null;
  const validLifecycle =
    lifecycleFilter &&
    serviceFacets(entities, 'lifecycle').some(f => f.key === lifecycleFilter)
      ? lifecycleFilter
      : null;
  return entities.filter(e => {
    if (validType && String(e.spec?.type ?? '').toLowerCase() !== validType) return false;
    if (validLifecycle && String(e.spec?.lifecycle ?? '').toLowerCase() !== validLifecycle) return false;
    return true;
  });
};

// Multi-select scope persistence. Reads an array; back-compat: a legacy
// single-string value ("my-work" or a team ref) is migrated transparently.
// Refs no longer present in `teamRefs` (the user left a team) are dropped so
// the picker never renders an orphan chip.
export const useScope = (teamRefs: string[]) => {
  const storageApi = useApi(storageApiRef).forBucket(SCOPE_STORAGE_BUCKET);
  const snapshot = useObservable(
    storageApi.observe$<string[] | string | { scopes?: string[]; subTeams?: boolean }>(SCOPE_STORAGE_KEY),
    storageApi.snapshot<string[] | string | { scopes?: string[]; subTeams?: boolean }>(SCOPE_STORAGE_KEY),
  );
  const raw = snapshot?.value;
  const teamSet = new Set(teamRefs);
  // Back-compat across the storage formats this PR went through:
  // string (single-select) -> string[] (multi-select) -> { scopes, subTeams }.
  let scopes: string[] = [];
  let subTeams = false;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as { scopes?: unknown; subTeams?: unknown };
    scopes = (Array.isArray(obj.scopes) ? obj.scopes : [])
      .filter((x): x is string => typeof x === 'string')
      .filter(r => teamSet.has(r));
    subTeams = obj.subTeams === true;
  } else if (Array.isArray(raw)) {
    scopes = (raw as unknown[])
      .filter((x): x is string => typeof x === 'string')
      .filter(r => teamSet.has(r));
  } else if (typeof raw === 'string' && raw !== '' && raw !== 'my-work') {
    scopes = teamSet.has(raw) ? [raw] : [];
  }
  const persist = (nextScopes: string[], nextSubTeams: boolean) => {
    const cleaned = Array.from(new Set(nextScopes.filter(r => teamSet.has(r))));
    storageApi.set(SCOPE_STORAGE_KEY, { scopes: cleaned, subTeams: nextSubTeams });
  };
  return {
    scopes,
    subTeams,
    setScopes: (next: string[]) => persist(next, subTeams),
    setSubTeams: (next: boolean) => persist(scopes, next),
  };
};

// The ownership refs a User entity implies: the user itself plus every team
// it is memberOf. Mirrors what the identity API claims for the signed-in
// user, derived instead from catalog relations so the "View as" lens can
// reconstruct any user's Home without their credentials.
export const ownershipFromUser = (user: Entity): string[] => [
  stringifyEntityRef(user),
  ...(user.relations ?? [])
    .filter(rel => rel.type === 'memberOf')
    .map(rel => rel.targetRef),
];

// One round-trip pulls: (a) every Component the user owns (across all teams)
// and (b) the Group entities for their teams. We need the Groups for the team
// avatars (`spec.profile.picture`, set by github-org). All filtering is
// in-memory after this, so chip toggles don't trigger fetches.
// `viewAsRef` swaps the identity claims for a catalog-derived reconstruction
// of another user's ownership (the "View as" lens).
export const useOwnedComponents = (viewAsRef: string | null = null) => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  return useAsync(async (): Promise<{
    entities: Entity[];
    teamRefs: string[];
    teamsByRef: Record<string, Entity>;
    currentUser?: Entity;
    childrenMap: Record<string, string[]>;
    directRefs: string[];
  }> => {
    let ownershipEntityRefs: string[];
    let userEntityRef: string | undefined;
    if (viewAsRef) {
      const viewed = await catalogApi
        .getEntityByRef(viewAsRef)
        .catch(() => undefined);
      ownershipEntityRefs = viewed ? ownershipFromUser(viewed) : [];
      userEntityRef = viewAsRef;
    } else {
      ({ ownershipEntityRefs, userEntityRef } =
        await identityApi.getBackstageIdentity());
    }
    const teamRefs = teamRefsFromOwnership(ownershipEntityRefs);
    if (!ownershipEntityRefs || ownershipEntityRefs.length === 0) {
      return { entities: [], teamRefs, teamsByRef: {}, currentUser: undefined, childrenMap: {}, directRefs: [] };
    }
    // Group hierarchy graph (~tens of entities): lets the sub-team toggle expand
    // a parent team into its transitive descendants without extra round-trips.
    const groupGraph = await catalogApi
      .getEntities({
        filter: { kind: 'Group' },
        fields: ['kind', 'metadata.name', 'metadata.namespace', 'relations'],
      })
      .then(r => r.items)
      .catch(() => [] as Entity[]);
    const childrenMap: Record<string, string[]> = {};
    for (const g of groupGraph) {
      const ref = stringifyEntityRef(g);
      const children = (g.relations ?? [])
        .filter(rel => rel.type === 'parentOf')
        .map(rel => rel.targetRef);
      if (children.length > 0) childrenMap[ref] = children;
    }
    // Fetch the MAXIMAL owned set once (own refs + every descendant team), so
    // toggling sub-teams on/off filters client-side without a refetch.
    const maximalRefs = expandScopes(ownershipEntityRefs, childrenMap);
    // Refs we want resolved entities for: every team Group (for chip avatars +
    // names) AND the signed-in User (for the "My work" chip avatar). One
    // batched fetch.
    const groupAndUserRefs = userEntityRef ? [...teamRefs, userEntityRef] : teamRefs;
    const [{ items }, teamItems] = await Promise.all([
      catalogApi.getEntities({
        filter: maximalRefs.map(ref => ({
          kind: 'Component',
          'relations.ownedBy': ref,
        })),
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.annotations',
          'spec.type',
          'spec.lifecycle',
          'relations',
        ],
      }),
      // Group entities for the user's teams; Groups failing to load is non-fatal
      // (TeamAvatar falls back to deterministic initials), so the call is
      // wrapped to never reject the whole load.
      groupAndUserRefs.length === 0
        ? Promise.resolve({ items: [] as (Entity | undefined)[] })
        : catalogApi
            .getEntitiesByRefs({
              entityRefs: groupAndUserRefs,
              fields: [
                'kind',
                'metadata.name',
                'metadata.namespace',
                'metadata.title',
                'spec.profile',
              ],
            })
            .catch(() => ({ items: [] as (Entity | undefined)[] })),
    ]);
    const teamsByRef: Record<string, Entity> = {};
    let currentUser: Entity | undefined;
    for (const e of teamItems.items) {
      if (!e) continue;
      if (e.kind === 'User') currentUser = e;
      else teamsByRef[stringifyEntityRef(e)] = e;
    }
    return { entities: items, teamRefs, teamsByRef, currentUser, childrenMap, directRefs: ownershipEntityRefs };
  }, [viewAsRef]);
};

